import React, { useEffect, useRef, useState } from 'react'
import { CreditCard, RefreshCw } from 'lucide-react'
import './CustomerPaymentPage.css'

const messages = {
  expired: 'This payment link has expired. Please ask the workshop for a new link.',
  unavailable: 'This payment request is no longer available. Please contact the workshop for the current bill.',
  paid: 'This bill has already been paid. No further payment is needed.',
  processing: 'A payment may already be in progress. Please wait for confirmation or contact the workshop before trying again.',
}

async function request(token, action, signal) {
  const response = await fetch('/.netlify/functions/customer-payment', {
    method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', signal,
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, action }),
  })
  const result = await response.json()
  if (!response.ok) throw new Error(result.error || messages.unavailable)
  return result
}

export default function CustomerPaymentPage() {
  const [token, setToken] = useState(() => window.location.hash.slice(1))
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  const version = useRef(0)
  useEffect(() => {
    const changed = () => setToken(window.location.hash.slice(1))
    window.addEventListener('hashchange', changed)
    return () => window.removeEventListener('hashchange', changed)
  }, [])
  useEffect(() => {
    const controller = new AbortController()
    const current = ++version.current
    setData(null); setBusy(true); setError('')
    request(token, 'inspect', controller.signal).then(result => {
      if (current === version.current) setData(result)
    }).catch(err => {
      if (err.name !== 'AbortError' && current === version.current) setError(err.message)
    }).finally(() => { if (current === version.current) setBusy(false) })
    return () => { controller.abort(); version.current++ }
  }, [token])
  const pay = async () => {
    const current = version.current
    setBusy(true); setError('')
    try {
      const result = await request(token, 'pay')
      if (current !== version.current) return
      if (result.status === 'ready' && /^https:\/\/checkout\.stripe\.com\//.test(result.checkout_url || '')) {
        window.location.assign(result.checkout_url)
      } else setData(result)
    } catch (err) { if (current === version.current) setError(err.message) }
    finally { if (current === version.current) setBusy(false) }
  }
  const money = value => new Intl.NumberFormat('en-GB', { style: 'currency', currency: data?.currency || 'GBP' }).format(value / 100)
  return <main className="customer-payment-page">
    <div className="brand">RE<span>BOUND</span></div>
    <section className="customer-payment-content" aria-busy={busy}>
      <h1>{data?.workshop || 'Workshop payment'}</h1>
      {data?.status === 'ready' ? <>
        <dl>{data.invoices.map((item, index) => <div key={index}><dt>{item.reference || 'Workshop service'}</dt><dd>{money(item.amount_pence)}</dd></div>)}</dl>
        <div className="customer-payment-total"><span>Total to pay</span><strong>{money(data.amount_pence)}</strong></div>
        <p className="text-muted">Link expires {new Date(data.expires_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}.</p>
        <button className="btn btn-primary" disabled={busy} onClick={pay}><CreditCard size={18} aria-hidden="true" />{busy ? 'Opening secure checkout...' : `Pay ${money(data.amount_pence)}`}</button>
      </> : <p role="status">{busy ? 'Checking payment request...' : messages[data?.status] || 'Payment request could not be loaded.'}</p>}
      {error && <p role="alert" className="alert alert-error">{error}</p>}
      {!data && !busy && <button className="btn btn-secondary" onClick={() => window.location.reload()}><RefreshCw size={16} aria-hidden="true" />Try again</button>}
    </section>
  </main>
}
